/**
 * @fileoverview PostHog tracking service implementation
 * @implements {TrackingService}
 */

/**
 * Implementation of the TrackingService interface for PostHog
 */
class PostHogService {
  /**
   * Create a new PostHogService instance
   */
  constructor() {
    /**
     * @type {boolean}
     * @private
     */
    this.initialized = false;

    /**
     * @type {TrackerDebugOptions}
     * @private
     */
    this.debugOptions = { enabled: false };
  }

  /**
   * Load PostHog script dynamically
   * @private
   * @returns {Promise<boolean>} - Promise resolving to true if script was loaded successfully
   */
  async loadPostHogScript() {
    return new Promise((resolve) => {
      // Check if PostHog is already loaded
      if (window.posthog && typeof window.posthog.init === "function") {
        resolve(true);
        return;
      }

      try {
        // Create posthog object if it doesn't exist
        !(function (t, e) {
          var o, n, p, r;
          e.__SV ||
            ((window.posthog = e),
            (e._i = []),
            (e.init = function (i, s, a) {
              function g(t, e) {
                var o = e.split(".");
                2 == o.length && ((t = t[o[0]]), (e = o[1])),
                  (t[e] = function () {
                    t.push(
                      [e].concat(Array.prototype.slice.call(arguments, 0)),
                    );
                  });
              }
              ((p = t.createElement("script")).type = "text/javascript"),
                (p.async = !0),
                (p.src = s.api_host + "/static/array.js"),
                (r =
                  t.getElementsByTagName("script")[0]).parentNode.insertBefore(
                  p,
                  r,
                );
              var u = e;
              for (
                void 0 !== a ? (u = e[a] = []) : (a = "posthog"),
                  u.people = u.people || [],
                  u.toString = function (t) {
                    var e = "posthog";
                    return (
                      "posthog" !== a && (e += "." + a),
                      t || (e += " (stub)"),
                      e
                    );
                  },
                  u.people.toString = function () {
                    return u.toString(1) + ".people (stub)";
                  },
                  o =
                    "capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys onSessionId".split(
                      " ",
                    ),
                  n = 0;
                n < o.length;
                n++
              )
                g(u, o[n]);
              e._i.push([i, s, a]);
            }),
            (e.__SV = 1));
        })(document, window.posthog || []);

        if (this.debugOptions.enabled) {
          console.log("[PostHogService] PostHog script loaded successfully");
        }

        resolve(true);
      } catch (error) {
        console.error("[PostHogService] Error loading PostHog script:", error);
        resolve(false);
      }
    });
  }

  /**
   * Initialize the PostHog tracking service
   * @param {Object} config - Configuration for PostHog
   * @param {string} config.apiKey - PostHog API key
   * @param {string} config.apiHost - PostHog API host
   * @param {boolean} [config.capturePageview=true] - Whether to capture page views automatically
   * @param {boolean} [config.autocapture=false] - Whether to autocapture events
   * @param {TrackerDebugOptions} [debugOptions] - Debug options
   * @returns {Promise<boolean>} - Promise resolving to true if initialization was successful
   */
  async initialize(config, debugOptions) {
    this.debugOptions = debugOptions || { enabled: false };

    if (!config.apiKey || !config.apiHost) {
      console.error(
        "[PostHogService] Missing required configuration: apiKey and apiHost",
      );
      return false;
    }

    try {
      // Load the PostHog script
      const scriptLoaded = await this.loadPostHogScript();
      if (!scriptLoaded) {
        console.error("[PostHogService] Failed to load PostHog script");
        return false;
      }

      // Configure PostHog
      const posthogConfig = {
        api_host: config.apiHost,
        capture_pageview: config.capturePageview !== false,
        autocapture: config.autocapture === true,
        persistence: "localStorage",
        debug: false,
      };

      // Initialize PostHog
      if (
        typeof window !== "undefined" &&
        window.posthog &&
        typeof window.posthog.init === "function"
      ) {
        window.posthog.init(config.apiKey, posthogConfig);

        if (this.debugOptions.enabled && this.debugOptions.logInitialization) {
          console.log("[PostHogService] Initialized with config:", {
            ...posthogConfig,
            api_key: "[REDACTED]",
          });
        }

        this.initialized = true;
        return true;
      } else {
        console.error(
          "[PostHogService] Failed to initialize: PostHog not available in window",
        );
        return false;
      }
    } catch (error) {
      console.error("[PostHogService] Error during initialization:", error);
      return false;
    }
  }

  /**
   * Track an event with PostHog
   * @param {TrackingEvent} event - The event to track
   * @returns {Promise<void>}
   */
  async trackEvent(event) {
    if (!this.initialized) {
      console.warn(
        "[PostHogService] Service not initialized. Event not tracked:",
        event.name,
      );
      return;
    }

    if (!event || !event.name) {
      console.error("[PostHogService] Invalid event:", event);
      return;
    }

    try {
      if (typeof window !== "undefined" && window.posthog) {
        window.posthog.capture(event.name, event.params || {});

        if (this.debugOptions.enabled && this.debugOptions.logEvents) {
          console.log(
            "[PostHogService] Tracked event:",
            event.name,
            event.params || {},
          );
        }
      }
    } catch (error) {
      console.error("[PostHogService] Error tracking event:", error);
    }
  }

  /**
   * Set user properties in PostHog
   * @param {UserProperties} properties - The user properties to set
   * @returns {Promise<void>}
   */
  async setUserProperties(properties) {
    if (!this.initialized) {
      console.warn(
        "[PostHogService] Service not initialized. User properties not set.",
      );
      return;
    }

    if (!properties) {
      console.error("[PostHogService] Invalid properties:", properties);
      return;
    }

    try {
      if (typeof window !== "undefined" && window.posthog) {
        // If we have a user ID, identify the user
        if (properties.user_id) {
          window.posthog.identify(properties.user_id);

          if (this.debugOptions.enabled) {
            console.log(
              "[PostHogService] User identified:",
              properties.user_id,
            );
          }
        }

        // Set the properties
        window.posthog.people.set(properties);

        if (this.debugOptions.enabled) {
          console.log("[PostHogService] User properties set:", {
            ...properties,
            user_email: properties.user_email ? "[REDACTED]" : undefined,
          });
        }
      }
    } catch (error) {
      console.error("[PostHogService] Error setting user properties:", error);
    }
  }

  /**
   * Reset the current user to an anonymous state
   * @returns {Promise<void>}
   */
  async resetUser() {
    if (!this.initialized) {
      console.warn("[PostHogService] Service not initialized. User not reset.");
      return;
    }

    try {
      if (typeof window !== "undefined" && window.posthog) {
        window.posthog.reset();

        if (this.debugOptions.enabled) {
          console.log("[PostHogService] User reset to anonymous");
        }
      }
    } catch (error) {
      console.error("[PostHogService] Error resetting user:", error);
    }
  }
}

export default PostHogService;
